import React, { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AdminImport() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const sel = {};
      data.rows.forEach((r, i) => { sel[i] = !r.duplicate; });
      setSelected(sel);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const commit = async () => {
    const rows = preview.rows.filter((_, i) => selected[i]);
    if (rows.length === 0) { toast.error("Select at least one product to import."); return; }
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/import/commit", { rows });
      setResult(data);
      setPreview(null);
      toast.success(`Imported ${data.created_count} product${data.created_count === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div data-testid="admin-import">
      <div className="flex items-center gap-3">
        <Upload className="w-8 h-8 text-brick" />
        <div>
          <h1 className="font-display text-4xl text-navy">Import Products</h1>
          <p className="text-ink/60 mt-1">Bring Facebook Marketplace / Vinted inventory into JohnnyMart in bulk — CSV, Excel or JSON. No SKUs needed.</p>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg border border-navy/15 p-6">
        <p className="text-sm text-ink/70">
          Your file needs a header row. Recognized columns: <b>Title/Name</b> (required), Description, Price, Category, Brand, Size, Condition, Quantity, Images (URLs separated by ; or ,), Source, Link.
          Photos from image URLs are downloaded and stored in JohnnyMart permanently. Items with a price + condition go live immediately; anything incomplete is saved as a <b>Draft</b> for you to finish.
        </p>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.json" onChange={onFile} className="hidden" data-testid="import-file-input" />
        <button data-testid="import-choose-file" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="mt-4 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3 hover:bg-navy transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Choose CSV / Excel / JSON
        </button>
      </div>

      {preview && (
        <div className="mt-6 bg-white rounded-lg border border-navy/15 p-6" data-testid="import-preview">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="font-bold text-navy">
              Preview: {preview.total} product{preview.total === 1 ? "" : "s"} found
              {preview.duplicates > 0 && <span className="text-amber-700"> · {preview.duplicates} possible duplicate{preview.duplicates === 1 ? "" : "s"} (unchecked below)</span>}
            </p>
            <button data-testid="import-commit-btn" onClick={commit} disabled={committing || selCount === 0}
              className="inline-flex items-center gap-2 bg-green-700 text-white font-bold uppercase rounded-md px-5 py-2.5 hover:bg-navy transition-colors disabled:opacity-60">
              {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Import {selCount} selected
            </button>
          </div>
          {preview.errors?.length > 0 && (
            <ul className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 space-y-1" data-testid="import-errors">
              {preview.errors.map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          )}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-navy text-cream text-left">
                <tr>
                  <th className="px-3 py-2">Import?</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Price</th>
                  <th className="px-3 py-2">Category</th><th className="px-3 py-2">Brand</th><th className="px-3 py-2">Size</th>
                  <th className="px-3 py-2">Condition</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Photos</th><th className="px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/10">
                {preview.rows.map((r, i) => (
                  <tr key={i} data-testid={`import-row-${i}`} className={r.duplicate ? "bg-amber-50" : ""}>
                    <td className="px-3 py-2">
                      <input type="checkbox" data-testid={`import-select-${i}`} checked={!!selected[i]}
                        onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })} />
                      {r.duplicate && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700"><AlertTriangle className="w-3 h-3" /> Duplicate?</span>}
                    </td>
                    <td className="px-3 py-2 font-semibold text-navy max-w-[220px] truncate">{r.name}</td>
                    <td className="px-3 py-2">{r.price != null ? `$${r.price.toFixed(2)}` : <span className="text-red-600 font-bold text-xs">missing → Draft</span>}</td>
                    <td className="px-3 py-2">{r.category || "—"}</td>
                    <td className="px-3 py-2">{r.brand || "—"}</td>
                    <td className="px-3 py-2">{r.size || "—"}</td>
                    <td className="px-3 py-2">{r.condition || <span className="text-red-600 font-bold text-xs">missing → Draft</span>}</td>
                    <td className="px-3 py-2">{r.quantity}</td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5 text-navy/40" /> {r.images.length}</span></td>
                    <td className="px-3 py-2">{r.source || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-6 bg-white rounded-lg border border-green-300 p-6" data-testid="import-result">
          <p className="font-bold text-green-700 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Imported {result.created_count} product{result.created_count === 1 ? "" : "s"}</p>
          <ul className="mt-3 text-sm text-ink/80 space-y-1">
            {result.created.map((c) => (
              <li key={c.inventory_id}><span className="font-mono font-bold text-brick">{c.inventory_id}</span> · {c.name} — {c.status}{c.images_stored ? ` · ${c.images_stored} photo${c.images_stored === 1 ? "" : "s"} stored` : ""}</li>
            ))}
          </ul>
          {result.failed_count > 0 && (
            <ul className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 space-y-1">
              {result.failed.map((f, i) => <li key={i}>• {f.name}: {f.reason}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
